/**
 * DatasetList Component
 *
 * Displays DCAT datasets in DIM UI's DataTable with:
 * - Dataset metadata (title, description, definition date, latest file date, filesize)
 * - Download, copy-link and Open-in-Yasgui action icon buttons
 * - Multi-select checkboxes for bulk zip download
 * - Search/filter by title
 * - Column sorting, and columns that don't fit moved into expandable rows
 */

import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Callout, Text } from '@bn-apps/design-system-ui-react';
import { Button, DataTable, SelectionBar, type DataTableColumn } from '@bn-apps/dim-ui-react';
import { Download, ContentCopy, ArrowOutward } from '@bn-apps/design-system-icons';
import { formatFileSize, downloadFile, downloadFilesAsZip } from '@services/files.service';
import { getIntlLocale } from '@utils/locale.utils';
import { buildYasguiUrl } from '@utils/yasgui.utils';
import { Combobox } from '@components/common/Combobox';
import type { ComboboxOption } from '@components/common/Combobox';
import { Accordion } from '@components/common/Accordion';
import { useMediaQuery } from '@hooks/useMediaQuery';
import type { Dataset } from '@/types';
import styles from './DatasetList.module.scss';

interface DatasetListProps {
  datasets: Dataset[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

function DatasetList({ datasets, loading, error, onRefresh }: DatasetListProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [copiedUri, setCopiedUri] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [filterTitles, setFilterTitles] = useState<ComboboxOption[]>([]);
  const [selectedDatasetUris, setSelectedDatasetUris] = useState<Set<string>>(new Set());
  const [downloadingZip, setDownloadingZip] = useState<boolean>(false);
  const locale = getIntlLocale(i18n.language);

  const titleOptions: ComboboxOption[] = useMemo(() => {
    const seen = new Set<string>();
    const options: ComboboxOption[] = [];
    for (const dataset of datasets) {
      if (!seen.has(dataset.uri)) {
        seen.add(dataset.uri);
        options.push({
          value: dataset.uri,
          label: dataset.title,
        });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [datasets]);

  const filteredData = useMemo(() => {
    if (filterTitles.length === 0) return datasets;
    const selectedUris = new Set(filterTitles.map((opt) => opt.value));
    return datasets.filter((dataset) => selectedUris.has(dataset.uri));
  }, [datasets, filterTitles]);

  const visibleSelected = useMemo(
    () => filteredData.filter((d) => selectedDatasetUris.has(d.uri)),
    [filteredData, selectedDatasetUris]
  );

  const handleDownloadSelected = useCallback(async (): Promise<void> => {
    if (!visibleSelected.length) return;
    setDownloadingZip(true);
    try {
      setDownloadError(null);
      await downloadFilesAsZip(visibleSelected, 'datasets.zip');
    } catch (err) {
      console.error('[DatasetList] Zip download failed:', err);
      setDownloadError(err instanceof Error ? err.message : t('datasets.download.error'));
    } finally {
      setDownloadingZip(false);
    }
  }, [visibleSelected, t]);

  const handleDownload = useCallback(async (dataset: Dataset): Promise<void> => {
    try {
      setDownloadError(null);
      const filename = dataset.filename || dataset.contentUrl.split('/').pop() || dataset.title;
      await downloadFile(dataset.contentUrl, filename);
    } catch (err) {
      console.error('[DatasetList] Download failed:', err);
      setDownloadError(err instanceof Error ? err.message : t('datasets.download.error'));
    }
  }, [t]);

  const handleCopyLink = useCallback(async (dataset: Dataset): Promise<void> => {
    try {
      // Prepend /download/ to match the nginx download endpoint
      const downloadPath = `/download${dataset.contentUrl}`;
      const fullUrl = new URL(downloadPath, window.location.origin).href;
      await navigator.clipboard.writeText(fullUrl);
      setCopiedUri(dataset.uri);
      setTimeout(() => setCopiedUri(prev => (prev === dataset.uri ? null : prev)), 2000);
    } catch (err) {
      console.error('[DatasetList] Copy link failed:', err);
    }
  }, []);

  const handleOpenInYasgui = useCallback((dataset: Dataset): void => {
    if (!dataset.sparqlQuery) return;
    const url = buildYasguiUrl(dataset.sparqlQuery, dataset.title);
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const formatDate = useCallback((date: Date): string =>
    new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(date)),
  [locale]);

  const formatDateTime = useCallback((date: Date): string =>
    new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date)),
  [locale]);

  const columns = useMemo<DataTableColumn<Dataset>[]>(() => [
    {
      id: 'title',
      header: t('datasets.table.title'),
      minWidth: 140,
      sortValue: (dataset) => dataset.title,
      cell: (dataset) => <strong>{dataset.title}</strong>,
    },
    {
      id: 'description',
      header: t('datasets.table.description'),
      minWidth: 160,
      priority: 4,
      sortValue: (dataset) => dataset.description,
      cell: (dataset) => dataset.description,
    },
    {
      id: 'definitionDate',
      header: t('datasets.table.definitionDate'),
      minWidth: 110,
      priority: 2,
      sortValue: (dataset) => new Date(dataset.definitionDate),
      cell: (dataset) => formatDate(dataset.definitionDate),
    },
    {
      id: 'fileCreated',
      header: t('datasets.table.fileCreated'),
      minWidth: 150,
      priority: 3,
      sortValue: (dataset) => new Date(dataset.fileCreated),
      cell: (dataset) => formatDateTime(dataset.fileCreated),
    },
    {
      id: 'filesize',
      header: t('datasets.table.filesize'),
      minWidth: 100,
      priority: 1,
      numeric: true,
      sortValue: (dataset) => Number(dataset.filesize),
      cell: (dataset) => formatFileSize(dataset.filesize),
    },
    {
      id: 'actions',
      header: t('datasets.table.actions'),
      hideHeader: true,
      minWidth: 150,
      cell: (dataset) => {
        const isCopied = copiedUri === dataset.uri;
        return (
          <div className={styles.actionButtons}>
            <button
              type="button"
              className={styles.iconButton}
              aria-label={t('datasets.download.aria', { filename: dataset.filename })}
              title={t('datasets.download.button')}
              onClick={() => void handleDownload(dataset)}
            >
              <Download />
            </button>
            <button
              type="button"
              className={`${styles.iconButton} ${isCopied ? styles.iconButtonCopied : ''}`}
              aria-label={t('datasets.download.copyLinkAria', { filename: dataset.filename })}
              title={isCopied ? t('datasets.download.copied') : t('datasets.download.copyLink')}
              onClick={() => void handleCopyLink(dataset)}
            >
              <ContentCopy />
            </button>
            {dataset.sparqlQuery && (
              <button
                type="button"
                className={styles.iconButton}
                aria-label={t('datasets.openInYasgui.buttonAria', { title: dataset.title })}
                title={t('datasets.openInYasgui.button')}
                onClick={() => handleOpenInYasgui(dataset)}
              >
                <ArrowOutward />
              </button>
            )}
          </div>
        );
      },
    },
  ], [t, copiedUri, formatDate, formatDateTime, handleDownload, handleCopyLink, handleOpenInYasgui]);

  const selectionBar = (
    <SelectionBar
      count={loading ? 0 : visibleSelected.length}
      label={(count) => t('datasets.selection.selectedCount', { count })}
      clearLabel={t('common.clear')}
      onClear={() => setSelectedDatasetUris(new Set())}
    >
      <Button
        className={styles.zipButton}
        hierarchy="primary"
        size="sm"
        iconStart={Download}
        isBusy={downloadingZip}
        onClick={handleDownloadSelected}
      >
        {t('datasets.selection.downloadZip')}
      </Button>
    </SelectionBar>
  );

  const filterControls = (
    <div className={styles.filterDataset}>
      <Combobox
        id="filter-dataset"
        label={t('datasets.filters.title')}
        options={titleOptions}
        selectedValues={filterTitles}
        onSelectionChange={setFilterTitles}
        placeholder={t('datasets.filters.placeholder')}
      />
    </div>
  );

  return (
    <div className="bn-mbe-1600">
      {downloadError && (
        <Callout
          calloutStatus="critical"
          title={t('datasets.download.error')}
          onDismiss={() => setDownloadError(null)}
        >
          {downloadError}
        </Callout>
      )}

      {/* Filter bar - accordion on mobile, grid on desktop */}
      {isMobile ? (
        <>
          <div className={styles.filterAccordionWrapper}>
            <Accordion heading={t('datasets.filters.accordionTitle')}>
              <div className={styles.filterBarMobile}>
                {filterControls}
              </div>
            </Accordion>
          </div>
          {visibleSelected.length > 0 && selectionBar}
        </>
      ) : (
        <div className={styles.filterBar}>
          {filterControls}
          <div className={styles.filterZip}>{selectionBar}</div>
        </div>
      )}

      <div className={styles.rowCountBar}>
        <Text fontSize="sm" style={{ visibility: loading ? 'hidden' : 'visible' }}>
          {t('datasets.count', { count: filteredData.length })}
        </Text>
      </div>

      <DataTable
        label={t('datasets.table.aria')}
        columns={columns}
        rows={filteredData}
        rowKey={(dataset) => dataset.uri}
        rowLabel={(dataset) => dataset.title}
        defaultSort={{ column: 'fileCreated', direction: 'descending' }}
        selectionMode="multiple"
        selectedKeys={selectedDatasetUris}
        onSelectionChange={setSelectedDatasetUris}
        selectOnRowClick
        maxHeight="600px"
        isLoading={loading}
        empty={t('datasets.empty')}
        error={error && t('datasets.error')}
        onRetry={onRefresh}
        labels={{
          selectAll: t('datasets.selection.selectAll'),
          selectRow: (title) => t('datasets.selection.selectRow', { title }),
          details: t('datasets.table.details'),
          showDetails: (title) => t('datasets.table.expandRow', { title }),
          retry: t('common.retry'),
        }}
      />
    </div>
  );
}

export default DatasetList;
